import { useState } from 'react'
import { Users } from 'lucide-react'
import { getInitials } from '../../utils/colors'
import { hasHandicapIndex, formatHandicapIndex } from '../../utils/handicaps'
import { useAuth } from '../../contexts/AuthContext'

/**
 * Renders search results: groups section + player results (followed + local).
 *
 * @param {Object} props
 * @param {Array} props.matchingFollowedPlayers - Followed players matching search
 * @param {Array} props.localSearchResults - Local players matching search
 * @param {Array} props.matchingGroups - Groups matching search
 * @param {Set} props.followedPlayerIds - IDs of followed players (for "Following" badge)
 * @param {Array} props.myFollows - All followed players (for badge check fallback)
 * @param {Function} props.onAddPlayer - Called with player ID when "Add" is clicked
 * @param {Function} props.onAddGroup - Called with group object when "Add All" is clicked
 * @param {boolean} props.isSearching - Whether search is active (1+ chars)
 * @param {boolean} [props.addPending] - Whether an add mutation is pending
 * @param {Set} [props.excludeIds] - Player IDs to exclude from group new-count
 */
export default function PlayerSearchResults({
  matchingFollowedPlayers,
  localSearchResults,
  matchingGroups,
  followedPlayerIds,
  myFollows,
  onAddPlayer,
  onAddGroup,
  isSearching,
  addPending,
  excludeIds,
}) {
  const { user } = useAuth()
  const [pressedAddId, setPressedAddId] = useState(null)
  const [pressedGroupId, setPressedGroupId] = useState(null)

  if (!isSearching) return null

  return (
    <>
      {/* Group search results */}
      {matchingGroups.length > 0 && (
        <div style={{
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '0.75rem',
          border: '1px solid var(--border-color)',
          padding: '0.75rem',
          marginBottom: '0.75rem',
        }}>
          <h3 style={{
            fontSize: '0.75rem',
            fontWeight: '600',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.5rem',
          }}>
            Groups
          </h3>
          {matchingGroups.map((group) => {
            const members = group.members || []
            const newCount = members.filter((m) => !excludeIds?.has(m.player_id)).length
            return (
              <div
                key={group.id}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.625rem',
                  padding: '0.625rem',
                  marginBottom: '0.25rem',
                  borderRadius: '0.375rem',
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                }}
              >
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--accent-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  flexShrink: 0,
                }}>
                  <Users size={14} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>{group.name}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {members.length} {members.length === 1 ? 'player' : 'players'}
                    {newCount < members.length && ` (${newCount} new)`}
                  </div>
                </div>
                <button
                  onClick={() => onAddGroup(group)}
                  onTouchStart={() => newCount > 0 && setPressedGroupId(group.id)}
                  onTouchEnd={() => setPressedGroupId(null)}
                  onTouchCancel={() => setPressedGroupId(null)}
                  disabled={addPending || newCount === 0}
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: newCount > 0 ? 'var(--accent-primary)' : 'var(--text-muted)',
                    padding: '0.25rem 0.625rem',
                    borderRadius: '9999px',
                    backgroundColor: newCount > 0 ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.1)' : 'var(--bg-primary)',
                    flexShrink: 0,
                    opacity: newCount === 0 ? 0.5 : pressedGroupId === group.id ? 0.6 : 1,
                    transform: pressedGroupId === group.id ? 'scale(0.9)' : 'scale(1)',
                    transition: 'transform 0.15s ease, opacity 0.15s ease',
                  }}
                >
                  {newCount === 0 ? 'Added' : 'Add All'}
                </button>
              </div>
            )
          })}
        </div>
      )}

      {/* Player search results */}
      {(matchingFollowedPlayers.length > 0 || localSearchResults.length > 0 || isSearching) && (
        <div style={{
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '0.75rem',
          border: '1px solid var(--border-color)',
          padding: '0.75rem',
          marginBottom: '1rem',
        }}>
          <h3 style={{
            fontSize: '0.75rem',
            fontWeight: '600',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.5rem',
          }}>
            Search Results
          </h3>
          {localSearchResults.length === 0 && matchingFollowedPlayers.length === 0 && (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', padding: '0.5rem 0' }}>
              No results found
            </p>
          )}
          {[...matchingFollowedPlayers, ...localSearchResults].map((player) => {
            const isManual = !player.user_id && player.created_by === user?.id
            const isFollowed = followedPlayerIds?.has(player.id) || (myFollows || []).some((f) => f.id === player.id)
            return (
              <div
                key={player.id}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.625rem',
                  padding: '0.625rem',
                  marginBottom: '0.25rem',
                  borderRadius: '0.375rem',
                  textAlign: 'left',
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                }}
              >
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: player.avatar_color || '#0ea47a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: '0.6875rem',
                  color: '#fff',
                  flexShrink: 0,
                }}>
                  {getInitials(player)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {isManual && (
                    <div style={{
                      fontSize: '0.5625rem',
                      fontWeight: '600',
                      color: 'var(--text-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: '0.125rem',
                    }}>
                      Manually Created
                    </div>
                  )}
                  <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                    {player.first_name || player.last_name
                      ? `${player.first_name || ''} ${player.last_name || ''}`.trim()
                      : player.display_name}
                  </div>
                  {player.display_name && (player.first_name || player.last_name) && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {player.display_name}
                    </div>
                  )}
                  {hasHandicapIndex(player.handicap_index) && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      HCP: {formatHandicapIndex(player.handicap_index)}
                    </div>
                  )}
                </div>
                {isFollowed && (
                  <span style={{
                    fontSize: '0.625rem',
                    fontWeight: '600',
                    color: 'var(--text-muted)',
                    padding: '0.125rem 0.375rem',
                    borderRadius: '0.25rem',
                    backgroundColor: 'var(--bg-primary)',
                    flexShrink: 0,
                  }}>
                    Following
                  </span>
                )}
                <button
                  onClick={() => onAddPlayer(player.id)}
                  onTouchStart={() => setPressedAddId(player.id)}
                  onTouchEnd={() => setPressedAddId(null)}
                  onTouchCancel={() => setPressedAddId(null)}
                  disabled={addPending}
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: 'var(--accent-primary)',
                    padding: '0.25rem 0.625rem',
                    borderRadius: '9999px',
                    backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.1)',
                    flexShrink: 0,
                    transform: pressedAddId === player.id ? 'scale(0.9)' : 'scale(1)',
                    opacity: pressedAddId === player.id ? 0.6 : 1,
                    transition: 'transform 0.15s ease, opacity 0.15s ease',
                  }}
                >
                  Add
                </button>
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}
